import React, { useState } from 'react';
import {
  CalendarCheck,
  Star,
  Clock,
  Sparkles,
  CheckCircle2,
  Crown,
  User,
  Video,
  MessageCircle,
  MapPin
} from 'lucide-react';
import { UserProfile, Tutor, TutoringBooking } from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { saveTutoringBooking } from '../lib/firebase';

interface TutoringSectionProps {
  userProfile: UserProfile;
  openPricingModal: () => void;
}

const CERTIFIED_TUTORS: Tutor[] = [
  {
    id: 'tutor_1',
    name: 'Dr. Sarah Jenkins, Ed.D.',
    title: 'Former ETS Exam Writer & Core Math Specialist',
    specialty: 'PRAXIS Core Math (5733) & Secondary Math (5165)',
    rating: 4.9,
    reviewsCount: 142,
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&auto=format&fit=crop&q=80',
    bio: '12+ years preparing pre-service teachers. Specialized in fraction/ratio speed tricks, algebra scaffolding, and overcoming math anxiety.',
    availableSlots: ['Today 4:00 PM EST', 'Tomorrow 10:00 AM EST', 'Thursday 2:00 PM EST', 'Friday 6:00 PM EST']
  },
  {
    id: 'tutor_2',
    name: 'Prof. Marcus Vance, M.Ed.',
    title: 'State Pedagogy & PLT Master Coach',
    specialty: 'Principles of Learning & Teaching (PLT 5621, 5622, 5624)',
    rating: 5.0,
    reviewsCount: 98,
    avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&auto=format&fit=crop&q=80',
    bio: 'Author of PLT Pedagogy Essentials. Expert in constructed response scenario scoring, Piaget/Vygotsky comparisons, and IEP team roles.',
    availableSlots: ['Tomorrow 1:00 PM EST', 'Wednesday 5:00 PM EST', 'Saturday 11:00 AM EST']
  },
  {
    id: 'tutor_3',
    name: 'Elena Rostova, M.A.',
    title: 'Special Education & Elementary Multi-Subject Specialist',
    specialty: 'Elementary (5001 - 5002-5005) & Special Ed (5354)',
    rating: 4.8,
    reviewsCount: 115,
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&auto=format&fit=crop&q=80',
    bio: 'Specialized in Science/Social Studies content memory mnemonics, IDEA/504 law distinctions, and phonemic awareness instruction.',
    availableSlots: ['Today 6:00 PM EST', 'Thursday 11:00 AM EST', 'Sunday 3:00 PM EST']
  }
];

export const TutoringSection: React.FC<TutoringSectionProps> = ({
  userProfile,
  openPricingModal
}) => {
  const [selectedTutor, setSelectedTutor] = useState<Tutor | null>(null);
  const [selectedPaperCode, setSelectedPaperCode] = useState<string>('5733');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [topicNotes, setTopicNotes] = useState<string>('');
  const [bookings, setBookings] = useState<TutoringBooking[]>([]);

  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Instant AI Tutor Query State
  const [aiQuery, setAiQuery] = useState<string>('');
  const [aiPaperCode, setAiPaperCode] = useState<string>('5733');
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [loadingAiResponse, setLoadingAiResponse] = useState<boolean>(false);

  // Free Consultation Request State (non-Unlimited users)
  const [consultationPaperCode, setConsultationPaperCode] = useState<string>('5733');
  const [consultationContact, setConsultationContact] = useState<string>('');
  const [consultationNotes, setConsultationNotes] = useState<string>('');
  const [isSubmittingConsultation, setIsSubmittingConsultation] = useState<boolean>(false);
  const [consultationSuccess, setConsultationSuccess] = useState<boolean>(false);
  const [consultationError, setConsultationError] = useState<string | null>(null);

  const isUnlimited = userProfile.tier === 'unlimited';

  const handleAskInstantAiTutor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;
    setLoadingAiResponse(true);
    setAiResponse(null);
    try {
      const res = await fetch('/api/tutor/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: aiQuery,
          paperCode: aiPaperCode,
          topic: '1-on-1 Pedagogy & Content Strategy'
        })
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        console.error('[TutoringSection AI Tutor Error]:', res.status, data.error || data);
        setAiResponse(`⚠️ AI Tutor Error: ${data.error || 'Failed to generate tutor answer.'}`);
      } else {
        setAiResponse(data.answer || 'No response returned.');
      }
    } catch (err: any) {
      console.error('[TutoringSection AI Tutor Network Error]:', err);
      setAiResponse(`⚠️ Connection Error: Could not reach AI Tutor. (${err?.message || 'Network error'})`);
    } finally {
      setLoadingAiResponse(false);
    }
  };

  const handleConfirmBooking = async () => {
    if (!selectedTutor || !selectedTimeSlot) return;

    setIsSubmitting(true);

    const newBooking: Omit<TutoringBooking, 'id'> = {
      userId: userProfile.uid,
      tutorId: selectedTutor.id,
      tutorName: selectedTutor.name,
      paperCode: selectedPaperCode,
      date: 'Upcoming',
      time: selectedTimeSlot,
      topic: topicNotes || 'General Exam Strategy Review',
      status: 'Confirmed',
      createdAt: new Date().toISOString()
    };

    const docId = await saveTutoringBooking(newBooking);
    setBookings(prev => [{ id: docId, ...newBooking }, ...prev]);

    setIsSubmitting(false);
    setBookingSuccess(true);
  };

  const handleRequestConsultation = async () => {
    if (!consultationPaperCode) return;

    setIsSubmittingConsultation(true);
    setConsultationError(null);

    // Note: TutoringBooking has no dedicated contact field, so the phone/WhatsApp
    // value is folded into the topic text (kept for admin visibility in Firestore)
    // and also sent as its own field in the notification payload below.
    const consultationTopic = consultationContact
      ? `[Contact: ${consultationContact}] ${consultationNotes || 'Free Consultation Request'}`
      : (consultationNotes || 'Free Consultation Request');

    const newConsultation: Omit<TutoringBooking, 'id'> = {
      userId: userProfile.uid,
      paperCode: consultationPaperCode,
      date: 'Upcoming',
      time: '',
      topic: consultationTopic,
      status: 'Pending',
      requestType: 'consultation',
      createdAt: new Date().toISOString()
    };

    try {
      await saveTutoringBooking(newConsultation);
      setConsultationSuccess(true);
      setConsultationPaperCode('5733');
      setConsultationContact('');
      setConsultationNotes('');

      // Fire-and-forget admin email notification -- a failed notify must never
      // undo or block the already-successful Firestore booking write above.
      fetch('/api/notify/tutoring-consultation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userProfile.uid,
          paperCode: newConsultation.paperCode,
          contact: consultationContact,
          notes: consultationNotes,
          createdAt: newConsultation.createdAt
        })
      }).catch(err => {
        console.error('[TutoringSection Consultation Notify Error]:', err);
      });
    } catch (err: any) {
      console.error('[TutoringSection Consultation Booking Error]:', err);
      setConsultationError(err?.message || 'Failed to submit consultation request. Please try again.');
    } finally {
      setIsSubmittingConsultation(false);
    }
  };

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-wider">
            <CalendarCheck className="w-4 h-4" /> 1-on-1 Live Expert Tutoring Sessions
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
            Personalized Guidance from PRAXIS Experts
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Schedule 1-on-1 video coaching sessions with certified PRAXIS exam authors and state pedagogy specialists.
          </p>
        </div>

        {!isUnlimited && (
          <button
            onClick={openPricingModal}
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs sm:text-sm px-4 py-2.5 rounded-xl transition shadow-xs flex items-center gap-2 shrink-0"
          >
            <Crown className="w-4 h-4" /> Included in Monthly Unlimited ($30/mo)
          </button>
        )}
      </div>

      {/* Tier Access Guard Banner if not unlimited */}
      {!isUnlimited && (
        <div className="p-6 rounded-3xl bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border border-amber-500/30 text-slate-900 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="font-extrabold text-base text-amber-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              1-on-1 Tutoring Requires Monthly Unlimited ($30/mo)
            </div>
            <p className="text-xs text-slate-700">
              Upgrade to the Monthly Unlimited tier to book unlimited 1-on-1 video tutoring sessions, essay reviews, and custom strategy calls.
            </p>
          </div>

          <button
            onClick={openPricingModal}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs px-6 py-3 rounded-2xl transition shadow-md whitespace-nowrap"
          >
            Upgrade to Unlimited ($30/mo)
          </button>
        </div>
      )}

      {/* Free Consultation Request Card (non-Unlimited signed-in users) */}
      {!isUnlimited && (
        <div className="p-6 rounded-3xl bg-white border border-indigo-200 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-indigo-700 font-bold text-sm">
            <MessageCircle className="w-5 h-5" />
            Request a Free Consultation
          </div>
          <p className="text-xs text-slate-600">
            Not ready to upgrade yet? Tell us which PRAXIS exam you're preparing for and a member of our team will reach out to discuss your study plan -- no cost, no commitment.
          </p>

          {consultationSuccess ? (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3 text-xs text-emerald-800">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div>
                <div className="font-bold">Request received!</div>
                <div>Our team will reach out shortly to schedule your free consultation.</div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Target PRAXIS Exam Paper</label>
                <select
                  value={consultationPaperCode}
                  onChange={(e) => setConsultationPaperCode(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-slate-900"
                >
                  {PRAXIS_PAPERS.map(p => (
                    <option key={p.id} value={p.code}>
                      PRAXIS {p.code}: {p.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Phone / WhatsApp (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. +1 555 123 4567"
                  value={consultationContact}
                  onChange={(e) => setConsultationContact(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-slate-900"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">What would you like to discuss?</label>
                <textarea
                  rows={3}
                  placeholder="e.g. Not sure which PRAXIS test I need, or when I should start studying..."
                  value={consultationNotes}
                  onChange={(e) => setConsultationNotes(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-slate-900"
                />
              </div>

              {consultationError && (
                <div className="text-red-600 font-semibold">{consultationError}</div>
              )}

              <button
                onClick={handleRequestConsultation}
                disabled={isSubmittingConsultation}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-2xl transition shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmittingConsultation ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-4 h-4" /> Request Free Consultation
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* 24/7 Instant AI Tutor Section */}
      <div className="bg-gradient-to-br from-indigo-900 via-slate-900 to-indigo-950 text-white rounded-3xl p-6 sm:p-8 shadow-xl border border-indigo-800/60 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b border-white/10">
          <div className="flex items-center gap-2 text-indigo-300 font-bold text-sm">
            <Sparkles className="w-5 h-5 text-amber-400 animate-pulse" />
            24/7 AI Instant PRAXIS Tutor
          </div>
          <span className="text-[11px] font-semibold bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 px-3 py-1 rounded-full">
            Powered by Gemini AI
          </span>
        </div>

        <p className="text-xs sm:text-sm text-slate-300">
          Need quick clarity before your live session? Ask our Gemini-powered AI tutor any question about PRAXIS formulas, pedagogy frameworks (Piaget, Vygotsky, Skinner), constructed responses, or state testing rules.
        </p>

        <form onSubmit={handleAskInstantAiTutor} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={aiPaperCode}
              onChange={(e) => setAiPaperCode(e.target.value)}
              className="bg-slate-800/90 text-white text-xs border border-slate-700 rounded-xl px-3 py-2.5 sm:w-48 focus:outline-none focus:border-indigo-400"
            >
              {PRAXIS_PAPERS.map(p => (
                <option key={p.id} value={p.code}>
                  PRAXIS {p.code}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="e.g. How do I distinguish Piaget's Concrete Operational vs Formal Operational stage?"
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              className="flex-1 bg-slate-800/90 text-white text-xs sm:text-sm border border-slate-700 rounded-xl px-4 py-2.5 placeholder-slate-400 focus:outline-none focus:border-indigo-400"
            />

            <button
              type="submit"
              disabled={loadingAiResponse || !aiQuery.trim()}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-xs sm:text-sm px-6 py-2.5 rounded-xl transition shadow-md disabled:opacity-50 flex items-center justify-center gap-2 shrink-0"
            >
              {loadingAiResponse ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Asking AI Tutor...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" /> Ask AI Tutor
                </>
              )}
            </button>
          </div>
        </form>

        {loadingAiResponse && (
          <div className="py-6 text-center text-xs text-indigo-300 space-y-2">
            <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto" />
            <p>Analyzing PRAXIS pedagogy standards and drafting step-by-step guidance...</p>
          </div>
        )}

        {aiResponse && !loadingAiResponse && (
          <div className="p-4 sm:p-5 rounded-2xl bg-white/10 border border-white/15 text-xs sm:text-sm text-slate-100 space-y-2 leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto">
            <div className="font-bold text-amber-300 text-xs border-b border-white/10 pb-2 flex items-center justify-between">
              <span>AI Tutor Explanation & Key Concepts</span>
              <button onClick={() => setAiResponse(null)} className="text-[10px] text-slate-400 hover:underline">
                Clear
              </button>
            </div>
            {aiResponse}
          </div>
        )}
      </div>

      {/* Tutors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {CERTIFIED_TUTORS.map(tutor => (
          <div
            key={tutor.id}
            className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xs flex flex-col justify-between space-y-4 hover:border-indigo-300 transition"
          >
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <img
                  src={tutor.avatar}
                  alt={tutor.name}
                  className="w-14 h-14 rounded-2xl object-cover border border-slate-200 shadow-xs"
                />
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">{tutor.name}</h3>
                  <p className="text-[11px] text-indigo-600 font-semibold">{tutor.title}</p>
                  <div className="flex items-center gap-1 text-amber-500 text-xs mt-0.5">
                    <Star className="w-3.5 h-3.5 fill-amber-400" />
                    <span className="font-bold text-slate-900">{tutor.rating}</span>
                    <span className="text-slate-400 font-normal">({tutor.reviewsCount} reviews)</span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-xs text-slate-700 font-medium">
                <strong>Specialty: </strong> {tutor.specialty}
              </div>

              <p className="text-xs text-slate-500 leading-relaxed">
                {tutor.bio}
              </p>
            </div>

            <button
              onClick={() => {
                if (!isUnlimited) {
                  openPricingModal();
                } else {
                  setSelectedTutor(tutor);
                  setBookingSuccess(false);
                }
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2.5 rounded-xl transition shadow-xs flex items-center justify-center gap-2"
            >
              <Video className="w-4 h-4" /> Book 1-on-1 Session
            </button>
          </div>
        ))}
      </div>

      {/* Active Bookings Tracker */}
      {bookings.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xs space-y-4">
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Your Scheduled Live Sessions ({bookings.length})
          </h3>

          <div className="space-y-3">
            {bookings.map(b => (
              <div key={b.id} className="p-4 bg-emerald-50/60 border border-emerald-200 rounded-2xl flex items-center justify-between text-xs">
                <div>
                  <div className="font-bold text-slate-900">Session with {b.tutorName}</div>
                  <div className="text-slate-600">Paper: PRAXIS {b.paperCode} | Time: {b.time}</div>
                  <div className="text-slate-500 italic mt-0.5">Topic: {b.topic}</div>
                </div>
                <span className="px-3 py-1 rounded-full bg-emerald-600 text-white font-bold text-[10px]">
                  {b.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {selectedTutor && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Book Session with {selectedTutor.name}</h3>
              <button onClick={() => setSelectedTutor(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            {!bookingSuccess ? (
              <div className="space-y-4 text-xs">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Target PRAXIS Exam Paper</label>
                  <select
                    value={selectedPaperCode}
                    onChange={(e) => setSelectedPaperCode(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-slate-900"
                  >
                    {PRAXIS_PAPERS.map(p => (
                      <option key={p.id} value={p.code}>
                        PRAXIS {p.code}: {p.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="font-bold text-slate-700 block mb-1">Select Available Time Slot</label>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedTutor.availableSlots.map(slot => (
                      <button
                        key={slot}
                        onClick={() => setSelectedTimeSlot(slot)}
                        className={`p-2.5 rounded-xl border text-xs font-semibold text-center transition ${
                          selectedTimeSlot === slot
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="font-bold text-slate-700 block mb-1">Specific Topics or Weak Areas to Focus On</label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Need help with Core Math 5733 fraction pacing or PLT scenario rubric..."
                    value={topicNotes}
                    onChange={(e) => setTopicNotes(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-slate-900"
                  />
                </div>

                <button
                  onClick={handleConfirmBooking}
                  disabled={!selectedTimeSlot || isSubmitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-2xl transition shadow-md disabled:opacity-50"
                >
                  {isSubmitting ? 'Confirming Booking...' : 'Confirm Live Tutoring Reservation'}
                </button>
              </div>
            ) : (
              <div className="py-6 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-slate-900 text-base">Reservation Confirmed!</h4>
                <p className="text-xs text-slate-600">
                  You are scheduled with {selectedTutor.name} for <strong>{selectedTimeSlot}</strong>. Video meeting link sent to your email.
                </p>
                <button
                  onClick={() => setSelectedTutor(null)}
                  className="bg-indigo-600 text-white font-bold px-6 py-2 rounded-xl text-xs"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
